import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';

import type { ItinerarioDia, Lugar, ModoTransporte } from '../api/api';
import type { RutaOSRMGeometry } from '../api/osrm';

type ExportOptions = {
  itinerarios: ItinerarioDia[];
  rutasOSRM: Array<RutaOSRMGeometry | null>;
  formatHour: (value?: string | Date | null) => string;
  colores?: string[];
};

type PdfOptions = ExportOptions & {
  colores: string[];
  modoTransporte: ModoTransporte;
  shareUrl?: string;
  dayMapImages?: Record<number, string>;
  createShareLink: () => Promise<string>;
};

const xmlEscape = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const getPlaceCoordinates = (lugares: Lugar[]): [number, number][] =>
  lugares
    .filter((lugar) => Number.isFinite(lugar.latitud) && Number.isFinite(lugar.longitud))
    .map((lugar) => [lugar.longitud, lugar.latitud]);

const getRouteCoordinatesForDay = (
  rutasOSRM: Array<RutaOSRMGeometry | null>,
  dayIndex: number,
  lugares: Lugar[],
): [number, number][] => {
  const geometry = rutasOSRM[dayIndex];
  if (geometry?.coordinates?.length) return geometry.coordinates;
  return getPlaceCoordinates(lugares);
};

const garminDisplayColors = [
  'Red',
  'Green',
  'Yellow',
  'Magenta',
  'DarkYellow',
  'Cyan',
  'DarkMagenta',
  'DarkRed',
  'DarkBlue',
  'Blue',
  'DarkCyan',
  'DarkGreen',
];

const HHMM_PATTERN = /^\d{2}:\d{2}$/;

const minutesFromTimeValue = (value?: string): number | null => {
  if (!value) return null;
  if (HHMM_PATTERN.test(value)) {
    const [hours, minutes] = value.split(':').map(Number);
    return hours * 60 + minutes;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getHours() * 60 + date.getMinutes();
};

const formatDurationBetween = (from?: string, to?: string): string | null => {
  const fromMinutes = minutesFromTimeValue(from);
  const toMinutes = minutesFromTimeValue(to);
  if (fromMinutes === null || toMinutes === null) return null;

  const minutes = Math.max(0, toMinutes - fromMinutes);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

const renderSvgToPng = (svg: string, width: number, height: number): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(svgUrl);
        reject(new Error('No se pudo preparar el mapa estatico.'));
        return;
      }

      ctx.drawImage(image, 0, 0, width, height);
      URL.revokeObjectURL(svgUrl);
      resolve(canvas.toDataURL('image/png'));
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      reject(new Error('No se pudo renderizar el mapa estatico.'));
    };

    image.src = svgUrl;
  });

const getImageDimensions = (src: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
    image.onerror = () => reject(new Error('No se pudieron leer las dimensiones de la imagen.'));
    image.src = src;
  });

const getFittedImageSize = async (src: string, maxWidth: number, maxHeight: number) => {
  const dimensions = await getImageDimensions(src);
  const imageRatio = dimensions.width / dimensions.height;
  let width = maxWidth;
  let height = width / imageRatio;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * imageRatio;
  }

  return { width, height };
};

const createStaticMapImage = async ({
  itinerarios,
  rutasOSRM,
  colores,
}: Pick<PdfOptions, 'itinerarios' | 'rutasOSRM' | 'colores'>): Promise<string | null> => {
  const width = 900;
  const height = 520;
  const margin = 52;
  const dayCoordinates = itinerarios.map((it, index) =>
    getRouteCoordinatesForDay(rutasOSRM, index, it.lugares),
  );
  const allCoordinates = dayCoordinates.flat();

  if (!allCoordinates.length) return null;

  const lons = allCoordinates.map(([lon]) => lon);
  const lats = allCoordinates.map(([, lat]) => lat);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const lonSpan = Math.max(maxLon - minLon, 0.001);
  const latSpan = Math.max(maxLat - minLat, 0.001);
  const project = ([lon, lat]: [number, number]) => {
    const x = margin + ((lon - minLon) / lonSpan) * (width - margin * 2);
    const y = height - margin - ((lat - minLat) / latSpan) * (height - margin * 2);
    return [x, y] as const;
  };

  const routePaths = dayCoordinates
    .map((coordinates, index) => {
      if (coordinates.length < 2) return '';
      const points = coordinates.map((coord) => project(coord).join(',')).join(' ');
      return `<polyline points="${points}" fill="none" stroke="${colores[index % colores.length]}" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" opacity="0.92"/>`;
    })
    .join('');

  const placeDots = itinerarios
    .map((it, dayIndex) =>
      it.lugares
        .map((lugar, placeIndex) => {
          const [x, y] = project([lugar.longitud, lugar.latitud]);
          return `<g>
            <circle cx="${x}" cy="${y}" r="15" fill="#fff" stroke="${colores[dayIndex % colores.length]}" stroke-width="5"/>
            <text x="${x}" y="${y + 5}" text-anchor="middle" font-family="Arial, sans-serif" font-size="13" font-weight="700" fill="#003057">${placeIndex + 1}</text>
          </g>`;
        })
        .join(''),
    )
    .join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
    <rect width="100%" height="100%" fill="#e6f1f2"/>
    <path d="M0 ${height * 0.68} C180 ${height * 0.58}, 260 ${height * 0.86}, 420 ${height * 0.68} S720 ${height * 0.4}, ${width} ${height * 0.52} V${height} H0 Z" fill="#f7f2ea"/>
    <path d="M0 ${height * 0.24} C180 ${height * 0.16}, 340 ${height * 0.34}, 520 ${height * 0.2} S760 ${height * 0.06}, ${width} ${height * 0.18}" fill="none" stroke="#c8d9cf" stroke-width="18" opacity=".7"/>
    ${routePaths}
    ${placeDots}
  </svg>`;

  return renderSvgToPng(svg, width, height);
};

export const downloadTextFile = (filename: string, content: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const buildGpxContent = ({
  itinerarios,
  rutasOSRM,
  formatHour,
  colores = ['#003057'],
}: ExportOptions): string => {
  const waypoints = itinerarios
    .flatMap((it) =>
      it.lugares.map(
        (lugar, index) => `  <wpt lat="${lugar.latitud}" lon="${lugar.longitud}">
    <name>${xmlEscape(`Dia ${it.dia} - ${index + 1}. ${lugar.nombre}`)}</name>
    <desc>${xmlEscape(`Llegada: ${formatHour(lugar.horaLlegada)} | Salida: ${formatHour(lugar.horaSalida)}`)}</desc>
  </wpt>`,
      ),
    )
    .join('\n');

  const tracks = itinerarios
    .map((it, index) => {
      const coordinates = getRouteCoordinatesForDay(rutasOSRM, index, it.lugares);
      if (coordinates.length < 2) return '';
      const color = colores[index % colores.length].replace('#', '').toUpperCase();
      const displayColor = garminDisplayColors[index % garminDisplayColors.length];
      const points = coordinates
        .map(([lon, lat]) => `      <trkpt lat="${lat}" lon="${lon}"></trkpt>`)
        .join('\n');

      return `  <trk>
    <name>${xmlEscape(`Dia ${it.dia}`)}</name>
    <extensions>
      <gpx_style:line>
        <gpx_style:color>${color}</gpx_style:color>
        <gpx_style:width>6</gpx_style:width>
      </gpx_style:line>
      <gpxx:TrackExtension>
        <gpxx:DisplayColor>${displayColor}</gpxx:DisplayColor>
      </gpxx:TrackExtension>
    </extensions>
    <trkseg>
${points}
    </trkseg>
  </trk>`;
    })
    .filter(Boolean)
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Ribeira Planificador" xmlns="http://www.topografix.com/GPX/1/1" xmlns:gpx_style="http://www.topografix.com/GPX/gpx_style/0/2" xmlns:gpxx="http://www.garmin.com/xmlschemas/GpxExtensions/v3">
${waypoints}
${tracks}
</gpx>
`;
};

export const buildKmlContent = ({
  itinerarios,
  rutasOSRM,
  formatHour,
  colores = ['#003057'],
}: ExportOptions): string => {
  const placemarks = itinerarios
    .flatMap((it) =>
      it.lugares.map(
        (lugar, index) => `    <Placemark>
      <name>${xmlEscape(`Dia ${it.dia} - ${index + 1}. ${lugar.nombre}`)}</name>
      <description>${xmlEscape(`Llegada: ${formatHour(lugar.horaLlegada)} | Salida: ${formatHour(lugar.horaSalida)}`)}</description>
      <Point><coordinates>${lugar.longitud},${lugar.latitud},0</coordinates></Point>
    </Placemark>`,
      ),
    )
    .join('\n');

  const routes = itinerarios
    .map((it, index) => {
      const coordinates = getRouteCoordinatesForDay(rutasOSRM, index, it.lugares);
      if (coordinates.length < 2) return '';
      const line = coordinates.map(([lon, lat]) => `${lon},${lat},0`).join(' ');
      const color = colores[index % colores.length].replace('#', '');
      const kmlColor = `ff${color.slice(4, 6)}${color.slice(2, 4)}${color.slice(0, 2)}`;

      return `    <Style id="route-${it.dia}">
      <LineStyle><color>${kmlColor}</color><width>4</width></LineStyle>
    </Style>
    <Placemark>
      <name>${xmlEscape(`Ruta dia ${it.dia}`)}</name>
      <styleUrl>#route-${it.dia}</styleUrl>
      <LineString><tessellate>1</tessellate><coordinates>${line}</coordinates></LineString>
    </Placemark>`;
    })
    .filter(Boolean)
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Ruta Ribeira</name>
${placemarks}
${routes}
  </Document>
</kml>
`;
};

export const buildItineraryPdf = async ({
  itinerarios,
  rutasOSRM,
  colores,
  modoTransporte,
  shareUrl,
  dayMapImages = {},
  createShareLink,
  formatHour,
}: PdfOptions) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  let cursorY = 18;

  const addTextBlock = (text: string, x: number, y: number, maxWidth: number, lineHeight = 6) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + lines.length * lineHeight;
  };

  const ensureSpace = (height: number) => {
    if (cursorY + height <= pageHeight - margin) return;
    doc.addPage();
    cursorY = 18;
  };

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(0, 48, 87);
  doc.text('Ruta Ribeira', margin, cursorY);
  cursorY += 9;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(70, 83, 96);
  doc.text(`Modo: ${modoTransporte}`, margin, cursorY);
  cursorY += 7;

  const hasCapturedDayMaps = Object.keys(dayMapImages).length > 0;
  const mapImage = hasCapturedDayMaps
    ? null
    : await createStaticMapImage({ itinerarios, rutasOSRM, colores });
  if (!hasCapturedDayMaps && mapImage) {
    const maxMapWidth = pageWidth - margin * 2;
    const maxMapHeight = 106;
    const { width: mapWidth, height: mapHeight } = await getFittedImageSize(
      mapImage,
      maxMapWidth,
      maxMapHeight,
    );
    const mapX = margin + (maxMapWidth - mapWidth) / 2;
    doc.addImage(mapImage, 'PNG', mapX, cursorY, mapWidth, mapHeight);
    cursorY += mapHeight + 8;
  }

  let qrUrl = shareUrl;
  try {
    qrUrl = qrUrl || (await createShareLink());
  } catch (err) {
    console.warn('No se pudo generar el enlace para el QR:', err);
  }

  if (qrUrl) {
    const qrDataUrl = await QRCode.toDataURL(qrUrl, { width: 160, margin: 1 });
    ensureSpace(38);
    doc.addImage(qrDataUrl, 'PNG', margin, cursorY, 28, 28);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(70, 83, 96);
    addTextBlock(
      'Escanea este QR para abrir la ruta compartida.',
      margin + 34,
      cursorY + 8,
      pageWidth - margin * 2 - 34,
    );
    cursorY += 36;
  }

  for (const it of itinerarios) {
    ensureSpace(24);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 48, 87);
    doc.text(`Dia ${it.dia}`, margin, cursorY);
    cursorY += 7;

    const dayMapImage = dayMapImages[it.dia];
    if (dayMapImage) {
      const maxMapWidth = pageWidth - margin * 2;
      const maxMapHeight = 112;
      const { width: mapWidth, height: mapHeight } = await getFittedImageSize(
        dayMapImage,
        maxMapWidth,
        maxMapHeight,
      );
      const mapX = margin + (maxMapWidth - mapWidth) / 2;
      ensureSpace(mapHeight + 8);
      doc.setDrawColor(171, 190, 209);
      doc.setLineWidth(0.25);
      doc.rect(mapX, cursorY, mapWidth, mapHeight);
      doc.addImage(dayMapImage, 'PNG', mapX, cursorY, mapWidth, mapHeight);
      cursorY += mapHeight + 8;
    }

    for (let index = 0; index < it.lugares.length; index += 1) {
      const lugar = it.lugares[index];
      ensureSpace(24);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 48, 87);
      cursorY = addTextBlock(`${index + 1}. ${lugar.nombre}`, margin, cursorY, pageWidth - margin * 2, 5);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(70, 83, 96);
      const horario = `Llegada: ${formatHour(lugar.horaLlegada)} | Salida: ${formatHour(lugar.horaSalida)}`;
      cursorY = addTextBlock(horario, margin + 4, cursorY, pageWidth - margin * 2 - 4, 5);

      const siguiente = it.lugares[index + 1];
      const traslado = siguiente ? formatDurationBetween(lugar.horaSalida, siguiente.horaLlegada) : null;
      if (siguiente) {
        const instruction = traslado
          ? `Despues, traslado hacia ${siguiente.nombre} (${traslado}).`
          : `Despues, traslado hacia ${siguiente.nombre}.`;
        cursorY = addTextBlock(instruction, margin + 4, cursorY, pageWidth - margin * 2 - 4, 5);
      }

      cursorY += 3;
    }
  }

  doc.save('ruta-ribeira.pdf');
};
